// import * as Sentry from "@sentry/nextjs"
import { useQueryClient } from "@tanstack/react-query"
import {
  createContext,
  type Dispatch,
  type FC,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react"

import {
  type BaseCommunityFragment,
  type BaseUserFragment,
  useUserQuery,
} from "../generated/graphql"
import { graphQLClient } from "../graphql/client"
import { retireActivityIdentity } from "../utils/activity"
import { identifyAmplitudeUser, resetAmplitude } from "../utils/amplitude"
import { AVAILABLE_COMMUNITIES } from "../utils/customization"
import { clearEventWithTicketsData } from "../utils/eventNavigationData"
import { isCheckoutPage } from "../utils/routing"
import { useAgnosticRouterInfo } from "../utils/useAgnosticRouterInfo"
import { useCommunityCustomization } from "../utils/useCommunityCustomization"

interface UserProviderProps {
  children: ReactNode | ReactNode[]
}

interface IContextProps {
  accessToken?: string
  communities: BaseCommunityFragment[]
  isInitialized: boolean
  refreshUser: () => Promise<BaseUserFragment | undefined | null>
  setAccessToken: Dispatch<SetStateAction<string | undefined>>
  setCommunities: (communities: BaseCommunityFragment[]) => void
  setUser: (user: BaseUserFragment | undefined | null) => void
  user?: BaseUserFragment
  logout: () => void
}

export const UserContext = createContext({} as IContextProps)

interface GraphQLErrorDetails {
  extensions?: {
    code?: string
  }
  message?: string
}

interface GraphQLRequestErrorLike {
  response?: {
    errors?: GraphQLErrorDetails[]
  }
}

const isMissingOrInvalidToken = (token: string | null | undefined) =>
  !token || token === "null" || token === "undefined"

interface AuthStateSnapshot {
  accessToken?: string
  communities: BaseCommunityFragment[]
  isInitialized: boolean
  user?: BaseUserFragment | null
}

const emptyAuthStateSnapshot: AuthStateSnapshot = {
  accessToken: undefined,
  communities: [],
  isInitialized: false,
  user: undefined,
}

let authStateSnapshot: AuthStateSnapshot = emptyAuthStateSnapshot

const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect

const filterAvailableCommunities = (communities: BaseCommunityFragment[]) =>
  communities.filter(
    (community) => community.code && AVAILABLE_COMMUNITIES.includes(community.code),
  )

const updateAuthStateSnapshot = (nextState: Partial<AuthStateSnapshot>) => {
  authStateSnapshot = {
    ...authStateSnapshot,
    ...nextState,
  }
}

const readPersistedAuthState = (): AuthStateSnapshot => {
  if (typeof window === "undefined") return emptyAuthStateSnapshot

  const persistedAccessToken = window.localStorage.getItem("accessToken")

  if (isMissingOrInvalidToken(persistedAccessToken)) {
    return {
      ...emptyAuthStateSnapshot,
      isInitialized: true,
    }
  }

  const persistedUser = window.localStorage.getItem("user")
  const persistedCommunities = window.localStorage.getItem("communities")

  return {
    accessToken: persistedAccessToken ?? undefined,
    communities: filterAvailableCommunities(
      persistedCommunities ? (JSON.parse(persistedCommunities) as BaseCommunityFragment[]) : [],
    ),
    isInitialized: true,
    user: persistedUser
      ? (JSON.parse(persistedUser) as BaseUserFragment | undefined | null)
      : undefined,
  }
}

const isUnauthenticatedError = (error: unknown) => {
  if (!error || typeof error !== "object") return false

  const errors = (error as GraphQLRequestErrorLike).response?.errors ?? []
  return errors.some((graphqlError) => {
    const message = graphqlError.message?.toLowerCase() ?? ""
    return (
      graphqlError.extensions?.code === "UNAUTHENTICATED" ||
      message === "unauthorized" ||
      message.includes("jwt") ||
      message.includes("invalid token")
    )
  })
}

const UserProvider: FC<UserProviderProps> = ({ children }) => {
  const { asPath } = useAgnosticRouterInfo()
  const customizationData = useCommunityCustomization()
  const queryClient = useQueryClient()
  const [initialAuthState] = useState(() => authStateSnapshot)
  const [accessToken, _setAccessToken] = useState<string | undefined>(initialAuthState.accessToken)
  const [user, _setUser] = useState<BaseUserFragment | undefined | null>(initialAuthState.user)
  const [communities, _setCommunities] = useState<BaseCommunityFragment[]>(
    initialAuthState.communities,
  )
  const [isInitialized, setIsInitialized] = useState(initialAuthState.isInitialized)

  const clearViewerSpecificEventData = useCallback(() => {
    clearEventWithTicketsData()
    queryClient.removeQueries({ queryKey: ["EventWithTickets"] })
  }, [queryClient])

  const clearAuthState = useCallback(() => {
    updateAuthStateSnapshot({
      accessToken: undefined,
      communities: [],
      isInitialized: true,
      user: undefined,
    })
    _setAccessToken(undefined)
    _setUser(undefined)
    _setCommunities([])
    graphQLClient.setHeader("Authorization", "")
    clearViewerSpecificEventData()
    queryClient.removeQueries({ queryKey: ["user"] })

    if (typeof window !== "undefined") {
      window.localStorage.removeItem("accessToken")
      window.localStorage.removeItem("user")
      window.localStorage.removeItem("communities")
    }
  }, [clearViewerSpecificEventData, queryClient])

  useIsomorphicLayoutEffect(() => {
    try {
      const persistedAuthState = readPersistedAuthState()

      if (isMissingOrInvalidToken(persistedAuthState.accessToken)) {
        clearAuthState()
        return
      }

      // EventWithTickets is viewer-specific. Install the persisted token before
      // enabling child queries, and discard anonymous/previous-viewer navigation
      // data so it cannot seed the paid-membership companion response.
      graphQLClient.setHeader("Authorization", persistedAuthState.accessToken ?? "")
      clearViewerSpecificEventData()
      updateAuthStateSnapshot(persistedAuthState)
      _setAccessToken(persistedAuthState.accessToken)
      _setUser(persistedAuthState.user)
      _setCommunities(persistedAuthState.communities)
    } catch {
      clearAuthState()
    } finally {
      setIsInitialized(true)
      updateAuthStateSnapshot({ isInitialized: true })
    }
  }, [clearAuthState, clearViewerSpecificEventData])

  // Every token change sets the header in the same step. Don't mirror accessToken into the
  // header from a passive effect: on a fresh load the first commit's effect runs after the
  // layout effect and clears the token, and child queries enabled in the re-render fetch
  // before the parent's effect restores it.
  const setAccessToken = (accessToken: string | undefined) => {
    clearViewerSpecificEventData()
    updateAuthStateSnapshot({ accessToken })
    _setAccessToken(accessToken)
    graphQLClient.setHeader("Authorization", accessToken ?? "")
    if (typeof window !== "undefined") {
      if (accessToken) {
        window.localStorage.setItem("accessToken", accessToken)
      } else {
        window.localStorage.removeItem("accessToken")
      }
    }
  }

  const { data, refetch } = useUserQuery(
    graphQLClient,
    {
      id: user?.id ?? "",
    },
    {
      enabled: !!user?.id && !!accessToken,
      onError: (error) => {
        if (isUnauthenticatedError(error)) {
          clearAuthState()
        }
      },
    },
  )

  useEffect(() => {
    if (user) {
      console.log("amplitude user identified")
      void identifyAmplitudeUser({
        email: user.email,
        id: user.id,
        username: user.username,
      })
    }
  }, [user])

  const setUser = (user: BaseUserFragment | undefined | null) => {
    updateAuthStateSnapshot({ user })
    _setUser(user)

    if (user?.id) {
      queryClient.setQueryData(["user", { id: user.id }], {
        user,
      })
    } else {
      queryClient.removeQueries({ queryKey: ["user"] })
    }

    if (typeof window !== "undefined") {
      if (user) {
        //   Sentry.setUser({ id: user.id })
        window.localStorage.setItem("user", JSON.stringify(user))
      } else {
        //   Sentry.setUser(null)
        window.localStorage.removeItem("user")
      }
    }
  }

  const setCommunities = (_communities: BaseCommunityFragment[]) => {
    const communities = filterAvailableCommunities(_communities)
    updateAuthStateSnapshot({ communities })
    if (typeof window !== "undefined") {
      window.localStorage.setItem("communities", JSON.stringify(communities))
    }
    _setCommunities(communities)
  }

  const refreshUser = useCallback(async () => {
    if (!user?.id) return user

    const result = await refetch()
    const nextUser = result.data?.user

    if (nextUser) {
      setUser(nextUser)
      if (nextUser.communitiesWhereMember.length > 0) {
        setCommunities(nextUser.communitiesWhereMember)
      }
    }

    return nextUser
  }, [refetch, setCommunities, user, setUser])

  const handleVisibilityChange = useCallback(() => {
    if (!document.hidden && user?.id) {
      void refreshUser()
    }
  }, [refreshUser, user?.id])

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [handleVisibilityChange])

  useEffect(() => {
    if (data?.user && user && JSON.stringify(data.user) !== JSON.stringify(user)) {
      // Don't overwrite if local user has profile fields that server data doesn't have yet
      // This can happen when the user just completed their profile and the query cache is stale
      if (
        (user.username && !data.user.username) ||
        (user.firstName && !data.user.firstName) ||
        (user.lastName && !data.user.lastName) ||
        (user.email && !data.user.email)
      ) {
        return
      }
      const sameEmail = user.email?.trim().toLowerCase() === data.user.email?.trim().toLowerCase()

      if (sameEmail && user.emailVerified && !data.user.emailVerified) {
        setUser({ ...data.user, emailVerified: true })
        return
      }

      setUser(data.user)
      // hacking this to not reset randomly
      // happens for first community join and going to checkout
      if (data.user.communitiesWhereMember.length > 0) {
        setCommunities(data.user.communitiesWhereMember)
      }
    }
  }, [data, setCommunities, setUser, user])

  const logout = () => {
    retireActivityIdentity()
    window.localStorage.clear()
    resetAmplitude()
    if (!isCheckoutPage(asPath)) {
      setUser(undefined)
      setCommunities([])
      setAccessToken(undefined)
      window.location.href = asPath.split("?")[0]
    } else {
      window.location.href = customizationData?.url ?? "/"
    }
  }

  const contextProps = {
    accessToken,
    communities,
    isInitialized,
    logout,
    refreshUser,
    setAccessToken,
    setCommunities,
    setUser,
    user,
  }

  return (
    <UserContext.Provider value={contextProps as IContextProps}>{children}</UserContext.Provider>
  )
}

export default UserProvider
